import type { ReactNode } from 'react';
import type { UseQueryResult } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';
import { RefreshCw } from 'lucide-react';
import { Button } from './Button';
import { Card } from './Card';
import { Skeleton } from './Feedback';
import { FormError } from './FormError';

export interface QueryStateProps<T> {
  /** A TanStack query, or several combined (same fields; refetch may return anything). */
  query: Pick<UseQueryResult<T>, 'data' | 'error' | 'isLoading' | 'isError' | 'isFetching'> & { refetch: () => unknown };
  /** Rendered when the data is "empty" (per `isEmpty`). */
  empty?: ReactNode;
  isEmpty?: (data: T) => boolean;
  /** Loading placeholder; defaults to skeleton lines. */
  loading?: ReactNode;
  children: (data: T) => ReactNode;
}

/** Loading → error (with retry) → empty → content, the same way on every screen. */
export function QueryState<T>({ query, empty, isEmpty, loading, children }: QueryStateProps<T>) {
  const { t } = useTranslation('common');
  if (!query.isError && (query.isLoading || query.data === undefined)) {
    return (
      <div role="status" aria-busy="true" aria-label={t('status.loading')}>
        {loading ?? (
          <Card>
            <Skeleton lines={4} />
          </Card>
        )}
      </div>
    );
  }
  if (query.isError || query.data === undefined) {
    return (
      <div className="flex flex-col items-start gap-3">
        <FormError error={query.error} className="w-full" />
        <Button variant="secondary" leadingIcon={RefreshCw} loading={query.isFetching} onClick={() => void query.refetch()}>
          {t('actions.retry')}
        </Button>
      </div>
    );
  }
  if (empty && isEmpty?.(query.data)) return <>{empty}</>;
  return <>{children(query.data)}</>;
}
